// Denial Review (app.md §9). Invoices denied at any approval step land here.
// Deliberately NO bulk actions and NO row-level buttons (decided 2026-06-10):
// the reviewer must Open each invoice and read the denier's comment in the
// decision history before acting — the actions live only in the modal's action
// bar.
//
// EVERY FIELD IS EDITABLE HERE (owner, 2026-10-08 — D-328: "The user reviewing
// an invoice at Denial Review should be able to change any and all of the
// fields, just like a user at Import Control can"). A reviewer who may decide
// here gets Import Control's editor (`editStep`), which owns the action bar;
// every edit is recorded in `corrections` with this step, the user and the time.
//
// TWO DECISIONS, ARCHIVE AND REINSTATE (owner, 2026-09-25: "At Denial Review,
// the user's options should be Archive and Reinstate"), and HOLD, immediately
// left of Archive (owner, 2026-10-08 — D-327: "The invoice modal at Denial
// Review should have a Hold button to the left of its Archive button"). From
// D-243 (2026-09-30) until then this step offered Release Hold only. A held
// invoice stays here, and its Archive and Reinstate wait for the release:
//
//   [Hold / Release Hold] [Archive] | [Reinstate]
//
//  * ARCHIVE IS AP/AR'S ARCHIVE (owner, 2026-09-28 — `docs/decisions.md`
//    D-185: "The Archive button at Denial Review should give the same pop-up
//    with the same options, as if the invoice had been selected to be Archived
//    at AP/AR Approval"). The same dialog — `ARCHIVE_WARNING`, the Category of
//    Blank, Duplicate, Not An Invoice or Other, the Reason required only under
//    Other — and the same outcome, `archived`. It replaced a
//    Category of Will Not Be Paid / Vendor Sending A New Invoice, whose two
//    stored outcomes are no longer written. Terminal.
//  * REINSTATE (`reinstated`) sends the invoice to the first qualifying
//    approval step, routed as Import Control's release routes it — Controller
//    Approval on a Controller rule, else Manager Approval, skipping what is
//    not staffed — with its values, comments and decision history (owner,
//    2026-10-08 — D-324). From D-312 until then it went back to Import
//    Control with a flag, and before D-312 to the step that denied it.
//    Reason required, no category.
//
// Each writes a Denial Review decision; the API moves the invoice.
import { DECISION_SOURCE } from '@ap/shared'
import { useMemo, useState } from 'react'
import { Button, Toast, ToastTitle, useToastController } from '@fluentui/react-components'
import { ScreenIntro } from '../components/ScreenIntro'
import type { Invoice } from '../lib/types'
import {
  ARCHIVE_CATEGORIES,
  ARCHIVE_WARNING,
  DecisionStep,
  DecisionOutcome,
  InvoiceStatus,
  SCREEN_SUBHEADER,
  statusLabel,
  TOASTER_ID,
} from '../lib/constants'
import { createDecision } from '../lib/data'
import type { ReasonCategory } from '../lib/constants'
import { REINSTATE_BODY } from '../lib/decisions'
import { errorMessage } from '../api/errors'
import { canDecide, filterDecidable, type Session } from '../lib/identity'
import type { RefreshSignal } from '../lib/refresh'
import { useQueue } from '../lib/hooks'
import { actionA11y, heldReason, useActiveHolds } from '../lib/holds'
import { InvoiceQueue } from '../components/InvoiceQueue'
import { InvoiceDetailModal } from '../components/InvoiceDetailModal'
import { DecisionDialog } from '../components/DecisionDialog'
import { ActionDivider } from '../components/ActionDivider'
import { HoldControl } from '../components/HoldControl'
import { applyFilters, type InvoiceFilter } from '../lib/filters'
import { Loading, EmptyQueue, ErrorBanner } from '../components/States'

/** The dialog in front of each action. */
type Pending = { kind: 'archive' | 'reinstate'; id: string } | null

const NOT_AUTHORIZED =
  "You don't hold the Denial Review role. You can read this invoice, but your decision would be refused."

interface Props {
  /** The signed-in session — the buttons mirror the API's gate (canDecide at
      the Denial Review step), like every other decision surface. */
  session: Session
  /** The shell's refresh signal (D-160): the Refresh and Pipeline buttons and the 30-second timer. */
  refresh?: RefreshSignal
  /** Page-wide filter (header Filter button; also drives the timeline badges). */
  filter: InvoiceFilter
  /** Called when acted rows leave the queue, so the timeline badges refresh too. */
  onQueuesChanged?: () => void
}

export function DenialReviewScreen({ session, refresh, filter, onQueuesChanged }: Props) {
  const user = session.user
  // Declared ahead of `useQueue`: the queue is PAUSED while any of these is
  // open or in flight, so a background refresh never moves the gallery under a
  // modal or dialog; it catches up silently on close (D-160).
  const [openInvoice, setOpenInvoice] = useState<Invoice | null>(null)
  const [pending, setPending] = useState<Pending>(null)
  /** What the dialog says, kept apart from whether it is open, so its words do
      not change during the close animation. */
  const [shown, setShown] = useState<Pending>(null)
  const [submitting, setSubmitting] = useState(false)
  const { invoices, loading, error, reload } = useQueue([InvoiceStatus.PendingDenialReview], refresh, {
    paused: openInvoice !== null || pending !== null || submitting,
  })
  const pageFiltered = useMemo(() => applyFilters(invoices, filter), [invoices, filter])
  // Owner ruling, 2026-08-14: only rows the caller can decide at this step
  // show at all — Denial Review is role-gated, so this is "every row, or
  // none", matching every other decision screen's rule. Before this, a
  // company-tier holder whose OWN company's records reached this status
  // (post-release, so their RLS scope already admits them) could see the
  // whole read-only queue despite holding no Denial Review authority at all.
  const filtered = useMemo(
    () => filterDecidable(session, DecisionStep.DenialReview, pageFiltered),
    [pageFiltered, session],
  )

  const [actionError, setActionError] = useState<string | null>(null)
  const { dispatchToast } = useToastController(TOASTER_ID)

  const byId = useMemo(() => new Map(invoices.map((i) => [i.id, i])), [invoices])

  // HOLDS (D-327): placed and released here by the Denial Review role. A held
  // invoice stays at this step until its hold is released.
  const { holdFor, heldIdsIn, setHoldFor } = useActiveHolds(invoices)
  // A row that says held is held before its hold record arrives (D-263).
  const heldIds = useMemo(() => heldIdsIn(filtered), [filtered, heldIdsIn])
  // The open invoice as the queue now has it, for the hold answer: the click
  // copy keeps the hold state of the moment it was read (D2-01, D1-05).
  const openInvoiceLive = openInvoice ? (byId.get(openInvoice.id) ?? openInvoice) : null
  // A reviewer who may decide here edits the invoice (D-328); anyone else is
  // shown it read-only, with the bar below disabled.
  const openDecidable = !!openInvoice && canDecide(session, DecisionStep.DenialReview, openInvoice)

  /** The step's Hold, for whichever bar is showing: the editor's, or the
      read-only fallback's. */
  function holdControlFor(inv: Invoice, decidable: boolean) {
    return (
      <HoldControl
        invoiceId={inv.id}
        hold={holdFor(inv)}
        decidable={decidable}
        notAuthorizedReason={NOT_AUTHORIZED}
        disabled={submitting}
        onError={setActionError}
        onChanged={(next) => {
          setHoldFor(inv.id, next)
          void reload({ silent: true })
          onQueuesChanged?.()
        }}
      />
    )
  }

  /** After the editor's Archive or Reinstate: the row leaves the queue. */
  function settle() {
    setOpenInvoice(null)
    void reload({ silent: true })
    onQueuesChanged?.()
  }

  function open(next: NonNullable<Pending>) {
    setShown(next)
    setPending(next)
  }


  async function submit(target: NonNullable<Pending>, reason: string, category?: string) {
    // If a background reload dropped the row, there is nothing to act on — say
    // it's gone rather than reporting a success.
    const inv = byId.get(target.id)
    if (!inv) {
      setPending(null)
      setOpenInvoice(null)
      setActionError('That invoice is no longer in the Denial Review queue — it was likely handled elsewhere. Refresh to reload the queue.')
      return
    }
    // Mirror the API's gate — a decision made without Denial Review authority is
    // refused, so don't send it.
    if (!canDecide(session, DecisionStep.DenialReview, inv)) {
      setPending(null)
      setActionError(NOT_AUTHORIZED)
      return
    }
    const outcome = target.kind === 'reinstate' ? DecisionOutcome.Reinstated : DecisionOutcome.Archived
    setSubmitting(true)
    setActionError(null)
    try {
      const result = await createDecision({
        invoice_id: inv.id,
        step: DecisionStep.DenialReview,
        decision: outcome,
        reason: reason || undefined,
        reason_category: target.kind === 'archive' ? ((category as ReasonCategory) || undefined) : undefined,
        row_version: inv.row_version,
        // Denial Review's buttons are all in the invoice modal (D-331).
        source: DECISION_SOURCE.modal,
      })
      setPending(null)
      setOpenInvoice(null)
      // Silent (owner, 2026-09-28): a loud reload swaps the list for a
      // placeholder, the page shrinks, and the reviewer is thrown to the top.
      void reload({ silent: true })
      onQueuesChanged?.()
      dispatchToast(
        <Toast>
          <ToastTitle>
            {outcome === DecisionOutcome.Reinstated
              ? `The invoice was reinstated to ${statusLabel(result.new_status)}.`
              : 'The invoice was archived.'}
          </ToastTitle>
        </Toast>,
        { intent: 'success' },
      )
    } catch (e) {
      setActionError(errorMessage(e))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <ScreenIntro>{SCREEN_SUBHEADER.DenialReview}</ScreenIntro>

      <ErrorBanner message={error || actionError} />

      {loading ? (
        <Loading label="Loading denied invoices…" />
      ) : filtered.length === 0 ? (
        <EmptyQueue
          text={
            pageFiltered.length > 0
              ? "You don't hold the Denial Review role."
              : invoices.length > 0
                ? 'No invoices match the current filters.'
                : undefined
          }
        />
      ) : (
        <InvoiceQueue
          invoices={filtered}
          screen={DecisionStep.DenialReview}
          columns="amounts"
          onOpen={setOpenInvoice}
          dimmedIds={heldIds}
          heldIds={heldIds}
          codingDisplay="halves"
        />
      )}

      <InvoiceDetailModal
        open={!!openInvoice}
        invoice={openInvoice}
        showPdf
        user={user}
        screen={DecisionStep.DenialReview}
        editStep={openDecidable ? DecisionStep.DenialReview : undefined}
        onChanged={() => void reload({ silent: true })}
        onClose={() => setOpenInvoice(null)}
        hold={holdFor(openInvoiceLive)}
        holdControl={openInvoiceLive && holdControlFor(openInvoiceLive, true)}
        // The editor's Reinstate reports where the invoice went, as Import
        // Control's Release does.
        onReleased={(landedStatus) => {
          settle()
          dispatchToast(
            <Toast>
              <ToastTitle>{`The invoice was reinstated to ${landedStatus ? statusLabel(landedStatus) : 'its first qualifying approval step'}.`}</ToastTitle>
            </Toast>,
            { intent: 'success' },
          )
        }}
        onArchived={() => {
          settle()
          dispatchToast(
            <Toast>
              <ToastTitle>The invoice was archived.</ToastTitle>
            </Toast>,
            { intent: 'success' },
          )
        }}
        actions={
          openInvoice &&
          (() => {
            const decidable = canDecide(session, DecisionStep.DenialReview, openInvoice)
            const hold = holdFor(openInvoiceLive)
            const held = !!hold
            const heldTitle = hold ? heldReason(hold) : undefined
            const title = heldTitle ?? (decidable ? undefined : NOT_AUTHORIZED)
            // A hold is the description; any other reason is in the name (QA C2-04).
            const why = { held: heldTitle, reason: decidable ? undefined : NOT_AUTHORIZED }
            return (
              <>
                {/* [HOLD] [ARCHIVE] | [REINSTATE] — the READ-ONLY fallback,
                    for a viewer who may not decide here; a reviewer who may
                    gets the editor's own bar in the same order (D-327,
                    D-328). The forward action is right of the divider, as at
                    every step (2026-09-29). */}
                {holdControlFor(openInvoiceLive ?? openInvoice, decidable)}
                <Button
                  disabled={submitting || !decidable || held}
                  title={title}
                  {...actionA11y('Archive', why)}
                  onClick={() => open({ kind: 'archive', id: openInvoice.id })}
                >
                  Archive
                </Button>
                <ActionDivider />
                <Button
                  disabled={submitting || !decidable || held}
                  title={title ?? 'Send this invoice to its first qualifying approval step.'}
                  {...actionA11y('Reinstate', why)}
                  onClick={() => open({ kind: 'reinstate', id: openInvoice.id })}
                >
                  Reinstate
                </Button>
              </>
            )
          })()
        }
      />

      <DecisionDialog
        open={!!pending}
        title="Are you sure?"
        body={shown?.kind === 'reinstate' ? REINSTATE_BODY : ARCHIVE_WARNING}
        requireReason
        reasonLabel="Comment"
        reasonPlaceholder={
          shown?.kind === 'reinstate'
            ? 'The vendor sent the missing receipt, so this can be approved.'
            : 'This document is a statement. There is nothing to be paid.'
        }
        /* ARCHIVE ASKS WHAT AP/AR'S ARCHIVE ASKS (D-185): the same four
           categories, the Reason required only under Other. Reinstate asks for
           no category, so its Reason is always required. */
        categories={shown?.kind === 'archive' ? ARCHIVE_CATEGORIES : undefined}
        confirmLabel={shown?.kind === 'reinstate' ? 'Reinstate' : 'Archive'}
        destructive={shown?.kind === 'archive'}
        submitting={submitting}
        onCancel={() => setPending(null)}
        onConfirm={(reason, category) => pending && void submit(pending, reason, category)}
      />
    </div>
  )
}
